// Media continuous work constraint
// For each day (by event start date) and each user, within any contiguous block
// of events where consecutive gaps are less than minBreak (default 20 minutes),
// the sum of assigned durations must be <= maxContinuousHours (default 6h).
//
// Requires model.optimizationIndexes:
// - users: { U# -> uid }
// - eventDateYmd: { E# -> 'YYYY-MM-DD' }
// - eventStartMs: { E# -> ms }
// - eventEndMs: { E# -> ms }
// - eventDuration: { E# -> hours }
// - staffRequiredByRole: { E# -> { R# -> count } }
// - decisionVariables: { name -> { userKey, eventKey, roleKey } }
export function buildMediaContinuousWorkConstraints(model, params = {}) {
  const idx = model?.optimizationIndexes || {};
  const usersMap = idx.users || {};
  const eventDateYmd = idx.eventDateYmd || {};
  const startMs = idx.eventStartMs || {};
  const endMs = idx.eventEndMs || {};
  // Prefer integer minutes to avoid fractional coefficients
  const durationMin = idx.eventEffectiveMinutes || idx.eventDurationMinutes || {};
  const byRole = idx.staffRequiredByRole || {};
  const declared = new Set(Object.keys(idx.decisionVariables || {}));

  const maxContinuousHours = Number(params?.maxContinuousHours) > 0 ? Number(params.maxContinuousHours) : 6;
  const minBreakMinutes = Number(params?.minBreakMinutes) > 0 ? Number(params.minBreakMinutes) : 20;
  const minBreakMs = minBreakMinutes * 60 * 1000;
  const maxContinuousMinutes = Math.round(maxContinuousHours * 60);

  // Group events by day (ymd)
  const byDay = new Map(); // ymd -> [{eKey, s, e}]
  for (const [eKey, ymd] of Object.entries(eventDateYmd)) {
    const s = startMs[eKey];
    const e = endMs[eKey];
    if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
    if (!byDay.has(ymd)) byDay.set(ymd, []);
    byDay.get(ymd).push({ eKey, s, e });
  }

  const constraints = [];

  for (const [, itemsRaw] of byDay.entries()) {
    const items = itemsRaw.slice().sort((a, b) => a.s - b.s);
    // Build contiguous blocks: consecutive events where gap < minBreakMs
    let block = [];
    const flushBlock = () => {
      if (block.length <= 0) return;
      // For each user, sum durations over vars in this block <= maxContinuousHours
      const eKeys = block.map((it) => it.eKey);
      for (const uKey of Object.keys(usersMap)) {
        const vars = [];
        const coeffs = [];
        for (const eKey of eKeys) {
          const roles = Object.keys(byRole[eKey] || {});
          // Compute per-event contribution to continuous work, adjusted for internal break
          const effMin = Math.max(0, Math.round(Number(durationMin[eKey] || 0)));
          const s = startMs[eKey];
          const e = endMs[eKey];
          const spanMin = (Number.isFinite(s) && Number.isFinite(e) && e > s) ? Math.round((e - s) / 60000) : effMin;
          const breakMinutes = Math.max(0, spanMin - effMin);
          const hasInternalBreak = breakMinutes >= minBreakMinutes;
          // If there is a qualifying internal break, conservatively cap the continuous contribution
          // Assume at least one split, so continuous work inside this event is at most half the effective time (rounded)
          const contMin = hasInternalBreak ? Math.max(0, Math.round(effMin / 2)) : effMin;
          for (const rKey of roles) {
            const name = `x_${uKey}_${eKey}_${rKey}`;
            if (!declared.has(name)) continue;
            vars.push(name);
            coeffs.push(contMin);
          }
        }
        if (vars.length) constraints.push({ linear: { vars, coeffs }, domain: [-100000000, maxContinuousMinutes] });
      }
      block = [];
    };

    for (let i = 0; i < items.length; i++) {
      const cur = items[i];
      // push current into ongoing block depending on gap with previous
      if (block.length === 0) {
        block.push(cur);
      } else {
        const prev = block[block.length - 1];
        const gap = cur.s - prev.e;
        if (gap < minBreakMs) {
          block.push(cur);
        } else {
          // Break is sufficient: close prior block and start new
          flushBlock();
          block.push(cur);
        }
      }
      // No explicit block split on internal break; per-event continuous contribution is already adjusted
    }
    flushBlock();
  }

  return constraints;
}
